<template>
  <v-card class="mb-0 versions-list" elevation="1">
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-source-branch</v-icon>
      <span class="font-weight-bold">Versions</span>
      <v-spacer />
      <v-btn
        color="success"
        data-testid="versions-create-btn"
        @click="openCreateDialog"
      >
        Add
      </v-btn>
    </v-card-title>

    <v-card-text class="versions-list__search py-1">
      <v-text-field
        label="Search"
        v-model="search"
        dense
        hide-details
        class="mt-0"
        prepend-icon="mdi-magnify"
      />
    </v-card-text>
    <v-data-table
      class="versions-list__table"
      sort-by="id"
      sort-desc
      :loading="items?.length === 0"
      :headers="headers"
      :items="items"
      :search="search"
      :items-per-page="10"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
    >
      <template v-slot:item.actions="{ item }">
        <div class="versions-list__actions">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="primary"
                v-bind="attrs"
                v-on="on"
                :data-testid="'versions-edit-' + item.id"
                @click="openEditDialog(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>Edit</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="secondary"
                tag="a"
                :href="changeLogHref(item)"
                target="_blank"
                rel="noopener noreferrer"
                :data-testid="'versions-history-' + item.id"
                v-bind="attrs"
                v-on="on"
              >
                mdi-history
              </v-icon>
            </template>
            <span>View change history</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="warning"
                v-bind="attrs"
                v-on="on"
                :data-testid="'versions-delete-' + item.id"
                @click="deleteItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.rollback="{ item }">
        <v-tooltip v-if="liveVersionId === item.id" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              small
              v-bind="attrs"
              v-on="on"
              :data-testid="'versions-rollback-' + item.id"
              @click="deployVersion(item.id, false, item)"
            >
              Rollback
            </v-btn>
          </template>
          <span>Rollback live version</span>
        </v-tooltip>
        <v-tooltip v-else-if="item.approved === false" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              small
              v-bind="attrs"
              v-on="on"
              :data-testid="'versions-deploy-' + item.id"
              @click="deployVersion(item.id, true, item)"
            >
              Deploy
            </v-btn>
          </template>
          <span>Deploy version</span>
        </v-tooltip>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip v-if="liveVersionId === item.id" small color="green">Live</v-chip>
        <v-chip v-else-if="item.approved" small color="primary">Was Live</v-chip>
      </template>
      <template v-slot:item.createdAt="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>
      <template v-slot:item.updatedAt="{ item }">
        {{ formatDate(item.updatedAt) }}
      </template>
      <template v-slot:item.approvedChangedAt="{ item }">
        {{ formatDate(item.approvedChangedAt) }}
      </template>
    </v-data-table>

    <!-- Create / Edit Version Dialog -->
    <v-dialog v-model="showDialog" max-width="640" persistent>
      <v-card class="admin-dialog">
        <v-card-title class="d-flex align-center admin-dialog__title">
          <span class="font-weight-bold">{{ isEditing ? 'Edit Game Version' : 'New Game Version' }}</span>
          <v-spacer />
          <v-btn icon small :disabled="isSaving" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="admin-dialog__body">
          <v-form ref="versionForm" v-model="formValid">
            <v-text-field
              v-model="formModel.version"
              label="Version"
              :rules="[rules.required]"
              :disabled="isEditing"
              dense
              outlined
              hide-details="auto"
              class="mb-3"
            />
            <v-text-field
              v-model="formModel.sdkVersion"
              label="SDK Version"
              hint="SDK version for this build package."
              persistent-hint
              dense
              outlined
              class="mb-3"
            />
            <v-switch
              v-if="isEditing"
              v-model="formModel.approved"
              label="Approved"
              class="mt-0"
              hide-details
              dense
              inset
              color="primary"
            />
            <v-file-input
              v-else
              v-model="formModel.file"
              label="Zip File"
              :disabled="game.renderingType === 'External'"
              dense
              outlined
              hide-details="auto"
              prepend-icon="mdi-paperclip"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="admin-dialog__actions">
          <v-spacer />
          <v-btn
            color="primary"
            class="mr-2"
            data-testid="version-form-save"
            :disabled="!formValid || isSaving"
            :loading="isSaving"
            @click="saveVersion"
          >
            {{ isEditing ? 'Update' : 'Save' }}
          </v-btn>
          <v-btn outlined :disabled="isSaving" @click="closeDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import persistentDialogEsc from '@/mixins/persistentDialogEsc';

export default {
  name: 'VersionList',
  components: {},
  mixins: [
    persistentDialogEsc({
      showDialog: { close: 'closeDialog', busy: 'isSaving' },
    }),
  ],
  props: {
    game: {},
    items: []
  },
  data() {
    return {
      search: '',
      showDialog: false,
      isSaving: false,
      formValid: false,
      formModel: {
        id: null,
        version: '',
        sdkVersion: '',
        approved: false,
        file: null
      },
      rules: {
        required: value => !!value || 'Required.'
      },
      headers: [
        {value: 'id', text: 'Id'},
        {value: 'version', text: 'Version'},
        {value: 'approved', text: 'Approved'},
        {value: 'createdAt', text: 'Created at'},
        {value: 'updatedAt', text: 'Updated at'},
        {value: 'approvedChangedAt', text: 'Approved changed at'},
        {value: 'rollback', text: 'Rollback'},
        {value: 'status', text: 'Status'},
        {
          value: 'actions',
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '1%',
          class: 'versions-list__col--compact',
          cellClass: 'versions-list__col--compact',
        },
      ],
    };
  },
  computed: {
    isEditing() {
      return !!this.formModel.id;
    },
    liveVersionId: function () {
      return this.items.filter(v => v.approved)?.sort((a, b) => {
        const [_, aMajor, aMinor] = a.version.match(/(\d+)\.(\d+)/) || [];
        const [__, bMajor, bMinor] = b.version.match(/(\d+)\.(\d+)/) || [];

        if (!aMinor && !bMinor) return b.id - a.id;
        if (!aMinor && bMinor) return -1;
        if (aMinor && !bMinor) return 1;

        if (bMajor > aMajor) return 1;
        if (aMajor > bMajor) return -1;
        if (bMinor > aMinor) return 1;
        if (aMinor > bMinor) return -1;

        return 0;
      })[0]?.id
    }
  },
  methods: {
    changeLogLink(item) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'game_version',
          entityId: String(item.id),
        },
      };
    },
    changeLogHref(item) {
      return this.$router.resolve(this.changeLogLink(item)).href;
    },
    openCreateDialog() {
      this.formModel = {
        id: null,
        version: '',
        sdkVersion: '',
        approved: false,
        file: null
      };
      this.showDialog = true;
      this.$nextTick(() => this.$refs.versionForm?.resetValidation?.());
    },
    openEditDialog(item) {
      this.formModel = {
        id: item.id,
        version: item.version,
        sdkVersion: item.sdkVersion || '',
        approved: !!item.approved,
        file: null
      };
      this.showDialog = true;
      this.$nextTick(() => this.$refs.versionForm?.resetValidation?.());
    },
    closeDialog() {
      if (this.isSaving) return;
      this.showDialog = false;
    },
    async saveVersion() {
      if (!this.formValid) return;
      this.isSaving = true;
      try {
        if (this.isEditing) {
          const payload = {
            ...this.items.find(v => v.id === this.formModel.id),
            version: this.formModel.version,
            approved: this.formModel.approved,
            sdkVersion: this.formModel.sdkVersion === '' || this.formModel.sdkVersion === undefined
              ? null
              : this.formModel.sdkVersion,
          };
          await this.$gameApi.patch(`/games/${this.game.id}/versions/${this.formModel.id}`, payload);
        } else {
          const formData = new FormData();
          formData.set('version', this.formModel.version);
          if (this.formModel.sdkVersion != null && String(this.formModel.sdkVersion).trim() !== '') {
            formData.set('sdkVersion', String(this.formModel.sdkVersion).trim());
          }
          if (this.formModel.file) {
            formData.set('file', this.formModel.file);
          }
          await this.$gameApi.post(`/games/${this.game.id}/versions/`, formData);
        }
        this.$swal.fire({ icon: 'success', title: 'Game version saved.', timer: 1500, showConfirmButton: false });
        this.showDialog = false;
        this.$emit('update');
      } catch (e) {
        this.$swal.fire({ icon: 'error', title: e.message, timer: 1500, showConfirmButton: false });
      } finally {
        this.isSaving = false;
      }
    },
    async deleteItem(item) {
      const {isConfirmed} = await this.$swal.fire({
        icon: 'warning',
        title: 'This will delete all feed information. Are you sure to proceed?',
        showCancelButton: true
      });
      if (isConfirmed) {
        await this.$gameApi.delete(`/games/${this.game.id}/versions/${item.id}`);
        this.$emit('update');
      }
    },
    deployVersion: async function (version, approved) {
      await this.$gameApi.patch(`/games/${this.game.id}/versions/${version}`, {approved});
      this.$emit('update');
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return (
          date.toLocaleString()
        );
      } catch (e) {
        return String(dateString);
      }
    },
  }
};
</script>

<style scoped lang="scss">
.versions-list {
  overflow: hidden;
}

.versions-list__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.versions-list__table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.versions-list__table ::v-deep table {
  min-width: 900px;
}

.versions-list__table ::v-deep tbody td {
  white-space: nowrap;
  vertical-align: middle;
}

.versions-list__table ::v-deep .versions-list__col--compact {
  white-space: nowrap;
  width: 1%;
  padding-left: 8px;
  padding-right: 8px;
}
</style>
